import React, {useCallback, useState} from "react";
import {useHistory, useParams} from "react-router-dom";
import {FormattedMessage} from "react-intl";
import {connect} from "react-redux";
import PropTypes from "prop-types";

import {useMediaQuery, useTheme} from "@mui/material";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";

import Page from "components/page/Page";
import StyledCard from "components/card/StyledCard";

import {MarketplaceRoutes} from "utils/MarketplaceRoutes";

import {greyishBlue500} from "assets/jss/main";

import KpiHeader from "./components/KpiHeader";
import ConversationListPane, {INITIAL_QUERY} from "./components/ConversationListPane";
import ConversationPane from "./Conversation/ConversationPane";

Workspace.propTypes = {
    member: PropTypes.object,
};

/** What decides which tab and filters a conversation belongs to. */
const membershipKey = (summary) => [summary.status, summary.handledBy, summary.suspendedAt, summary.erasureRequestedAt, (summary.openEscalations || []).join(","), summary.retention?.expiringSoon].join("|");

/**
 * The personal shopper workspace: the key figures on top, then three columns, the
 * conversations (tabs, search, filters), the open thread with its reply box, and the
 * customer sheet. On a narrow screen, the list and the open conversation take turns.
 */
function Workspace({member}) {
    const {id} = useParams();
    const history = useHistory();
    const theme = useTheme();
    const narrow = useMediaQuery(theme.breakpoints.down("lg"));
    // The list's query lives here so a narrow screen can show the conversation alone and come back to it.
    const [query, setQuery] = useState(INITIAL_QUERY);
    // id => the latest summary of a conversation opened here, so its card follows its actions.
    const [patches, setPatches] = useState({});
    // Bumped when an action changed what a tab or a filter holds: the list is fetched again.
    const [refreshKey, setRefreshKey] = useState(0);

    const selectedId = id ? Number(id) : null;
    const select = (conversationId) => history.push(MarketplaceRoutes.PERSONAL_SHOPPER_CONVERSATION(conversationId));
    const backToList = () => history.push(MarketplaceRoutes.PERSONAL_SHOPPER);
    const onSummaryChange = useCallback((summary) => {
        setPatches((previous) => {
            const known = previous[summary.id];
            if (known && membershipKey(known) !== membershipKey(summary)) {
                setRefreshKey((key) => key + 1);
            }

            return {...previous, [summary.id]: summary};
        });
    }, []);
    // A fresh page is the truth for every card but the open conversation's, which is kept live.
    const onFetched = useCallback(() => setPatches((previous) => (selectedId !== null && previous[selectedId] ? {[selectedId]: previous[selectedId]} : {})), [selectedId]);
    const onErased = (erasedId) => {
        setPatches((previous) => {
            const {[erasedId]: gone, ...kept} = previous;

            return kept;
        });
        setRefreshKey((key) => key + 1);
        backToList();
    };

    const showList = !narrow || selectedId === null;

    return (
        <Page titleId="personalShopper.title" subtitleId="personalShopper.subtitle" fullPage>
            <KpiHeader />
            <div style={{display: "grid", gridTemplateColumns: narrow ? "minmax(0, 1fr)" : "340px minmax(0, 1fr) 380px", gap: "16px", marginTop: "16px", alignItems: "start"}}>
                {showList && (
                    <ConversationListPane
                        selectedId={selectedId}
                        memberEmail={member?.email}
                        query={query}
                        onQueryChange={setQuery}
                        refreshKey={refreshKey}
                        patches={patches}
                        onFetched={onFetched}
                        onSelect={select}
                    />
                )}
                {selectedId !== null ? (
                    <ConversationPane
                        key={selectedId}
                        id={selectedId}
                        memberEmail={member?.email}
                        narrow={narrow}
                        onBack={backToList}
                        onSummaryChange={onSummaryChange}
                        onErased={() => onErased(selectedId)}
                    />
                ) : (!narrow && (
                    <>
                        <StyledCard noHoverShadow style={{minHeight: "60vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: greyishBlue500, gap: "8px"}}>
                            <ForumOutlinedIcon sx={{fontSize: 40}} />
                            <FormattedMessage id="personalShopper.workspace.select" />
                        </StyledCard>
                        <div />
                    </>
                ))}
            </div>
        </Page>
    );
}

const mapStateToProps = (state) => ({
    member: state.authentication.member,
});

export default connect(mapStateToProps)(Workspace);
